import { IonIcon } from "@ionic/react";
import React from "react";
import clsx from "clsx";
import {
  chevronDownOutline as downIcon,
  chevronUpOutline as upIcon,
} from "ionicons/icons";

import { IClass, IGearboxTeacher, IRealization, ITeacher } from "../../state";
import checkIcon from "../../assets/icons/check.svg";

import styles from "./TargetGroup.module.scss";

export enum ETargetSwitch {
  ONLY_ME = "自分のみ",
  GROUP = "クラス・グループ",
  TEACHER = "先生",
}

export interface TargetGroupProps {
  realization?: IRealization;
  teachers: ITeacher[];
  classes: IClass[];
  showShareForm: boolean;
  showSubmitForm: boolean;
  targetSwitch: ETargetSwitch | null;
  selected_class_ids: number[];
  selected_teacher_ids: number[];
  disabled?: boolean;
  excludeRef?: React.RefObject<HTMLElement>;
  update: (args: { [key: string]: any }) => void;
  stock_c_to_c_enabled?: boolean;
}

const MAX_NAME_LENGTH = 10;

const formatSelectedLabel = (names: string[]): string => {
  if (names.length === 0) return "";
  const first = names[0];
  const truncated =
    first.length > MAX_NAME_LENGTH
      ? `${first.slice(0, MAX_NAME_LENGTH)}…`
      : first;
  if (names.length === 1) return truncated;
  return `${truncated} 他${names.length - 1}`;
};

const TargetGroup: React.FC<TargetGroupProps> = ({
  realization,
  teachers,
  classes,
  showShareForm,
  showSubmitForm,
  targetSwitch,
  selected_class_ids,
  selected_teacher_ids,
  disabled,
  excludeRef,
  update,
  stock_c_to_c_enabled,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const groupListRef = React.useRef<HTMLDivElement>(null);
  const teacherListRef = React.useRef<HTMLDivElement>(null);
  const groupWrapperRef = React.useRef<HTMLDivElement>(null);
  const teacherWrapperRef = React.useRef<HTMLDivElement>(null);

  const handleOpenDropdown = React.useCallback(
    (value: ETargetSwitch) => {
      const isTeacher = value === ETargetSwitch.TEACHER;
      const isGroup = value === ETargetSwitch.GROUP;
      const shouldOpenSubmit = isTeacher
        ? targetSwitch === ETargetSwitch.TEACHER
          ? !showSubmitForm
          : true
        : false;
      const shouldOpenShare = isGroup
        ? targetSwitch === ETargetSwitch.GROUP
          ? !showShareForm
          : true
        : false;

      update({
        showCategoryForm: false,
        showSubmitForm: shouldOpenSubmit,
        showShareForm: shouldOpenShare,
        selected_teacher_ids: isTeacher ? selected_teacher_ids : [],
        selected_class_ids: isGroup ? selected_class_ids : [],
        targetSwitch: value,
      });
    },
    [
      targetSwitch,
      showSubmitForm,
      showShareForm,
      selected_teacher_ids,
      selected_class_ids,
      update,
    ],
  );

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (excludeRef?.current && excludeRef.current.contains(e.target as Node))
        return;
      if (showShareForm) {
        if (
          groupWrapperRef.current &&
          groupWrapperRef.current.contains(e.target as Node)
        )
          return;
        if (
          groupListRef.current &&
          !groupListRef.current.contains(e.target as Node)
        ) {
          update({ showShareForm: false, showSubmitForm: false });
          return;
        }
      }
      if (showSubmitForm) {
        if (
          teacherWrapperRef.current &&
          teacherWrapperRef.current.contains(e.target as Node)
        )
          return;
        if (
          teacherListRef.current &&
          !teacherListRef.current.contains(e.target as Node)
        ) {
          update({ showShareForm: false, showSubmitForm: false });
          return;
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareForm, showSubmitForm, excludeRef, update]);

  const teacherList = React.useMemo(
    () =>
      [...teachers, ...(realization?.submitted_teachers ?? [])].reduce(
        (uniqueList: IGearboxTeacher[], teacher) =>
          !uniqueList.some(u => u.id === teacher.id)
            ? [
                ...uniqueList,
                { id: teacher.id, full_name: teacher.full_name ?? "" },
              ]
            : uniqueList,
        [],
      ),
    [realization?.submitted_teachers, teachers],
  );

  const handleSelectTeacher = React.useCallback(
    (teacherId: number) => {
      const next = selected_teacher_ids.includes(teacherId)
        ? selected_teacher_ids.filter(id => id !== teacherId)
        : [...selected_teacher_ids, teacherId];
      update({ selected_teacher_ids: next });
    },
    [selected_teacher_ids, update],
  );

  const handleSelectClass = React.useCallback(
    (classId: number) => {
      const next = selected_class_ids.includes(classId)
        ? selected_class_ids.filter(id => id !== classId)
        : [...selected_class_ids, classId];
      update({ selected_class_ids: next });
    },
    [selected_class_ids, update],
  );

  const selectedClassNames = selected_class_ids
    .map(id => classes.find(classItem => classItem.id === id))
    .filter(classItem => classItem !== undefined)
    .map(classItem => classItem.full_name);

  const selectedTeacherNames = selected_teacher_ids
    .map(id => teacherList.find(teacher => teacher.id === id))
    .filter(teacher => teacher !== undefined)
    .map(teacher => teacher.full_name);

  const classLabel =
    selectedClassNames.length > 0
      ? formatSelectedLabel(selectedClassNames)
      : ETargetSwitch.GROUP;

  const teacherLabel =
    selectedTeacherNames.length > 0
      ? formatSelectedLabel(selectedTeacherNames)
      : ETargetSwitch.TEACHER;

  return (
    <div
      className={clsx(styles.wrapper, { [styles.disabled]: disabled })}
      ref={wrapperRef}
      onClick={e => e.stopPropagation()}
    >
      <span className={styles.label}>提出先</span>
      <div className={styles.targetSwitch}>
        <button
          type="button"
          className={clsx(styles.switchItem, {
            [styles.switchItemActive]: targetSwitch === ETargetSwitch.ONLY_ME,
          })}
          onClick={() => handleOpenDropdown(ETargetSwitch.ONLY_ME)}
        >
          {ETargetSwitch.ONLY_ME}
        </button>
        <div className={styles.dropdownWrapper} ref={groupWrapperRef}>
          <button
            type="button"
            className={clsx(styles.switchItem, {
              [styles.switchItemActive]: targetSwitch === ETargetSwitch.GROUP,
            })}
            onClick={() => handleOpenDropdown(ETargetSwitch.GROUP)}
          >
            {classLabel}
            <IonIcon
              icon={
                targetSwitch === ETargetSwitch.GROUP && showShareForm
                  ? upIcon
                  : downIcon
              }
              className={styles.chevron}
            />
          </button>
          {targetSwitch === ETargetSwitch.GROUP &&
            showShareForm &&
            classes.length > 0 && (
              <div ref={groupListRef} className={styles.dropdownList}>
                {stock_c_to_c_enabled && (
                  <div className={styles.groupTitle}>
                    ※他校の生徒にとって参考になるストックは、匿名で他校に共有されることがあります
                  </div>
                )}
                <ul>
                  {classes.map(cls => (
                    <li
                      key={cls.id}
                      className={styles.dropdownItem}
                      onClick={() => handleSelectClass(cls.id)}
                    >
                      <span>{cls.full_name}</span>
                      {selected_class_ids.includes(cls.id) && (
                        <IonIcon
                          icon={checkIcon}
                          className={styles.checkIcon}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
        <div className={styles.dropdownWrapper} ref={teacherWrapperRef}>
          <button
            type="button"
            className={clsx(styles.switchItem, {
              [styles.switchItemActive]: targetSwitch === ETargetSwitch.TEACHER,
            })}
            onClick={() => handleOpenDropdown(ETargetSwitch.TEACHER)}
          >
            {teacherLabel}
            <IonIcon
              icon={
                targetSwitch === ETargetSwitch.TEACHER && showSubmitForm
                  ? upIcon
                  : downIcon
              }
              className={styles.chevron}
            />
          </button>
          {targetSwitch === ETargetSwitch.TEACHER &&
            showSubmitForm &&
            teacherList.length > 0 && (
              <div ref={teacherListRef} className={styles.dropdownList}>
                <ul>
                  {teacherList.map(teacher => (
                    <li
                      key={teacher.id}
                      className={styles.dropdownItem}
                      onClick={() => handleSelectTeacher(teacher.id)}
                    >
                      <span>{teacher.full_name}</span>
                      {selected_teacher_ids.includes(teacher.id) && (
                        <IonIcon
                          icon={checkIcon}
                          className={styles.checkIcon}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
export default TargetGroup;
